import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { TypedMemo } from '../../../../common/types';
import { ConfirmDialog } from '../../../../common/views/modals/confirmDialog/ConfirmDialog';
import { ConfirmDialogTileMark } from '../../../../common/views/modals/confirmDialog/ConfirmDialogMark';
import { useConfirmWhileBusy } from '../../../../common/views/modals/confirmDialog/useConfirmWhileBusy';
import { ConnectedApp } from './useConnectedApps';
import { ConnectedAppMark } from './ConnectedAppMark';
import { aiAssistantsMessages } from '../aiAssistantsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the connected-apps parts; each part uses its own slice of it */
import styles from './connectedApps.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ConnectedAppRowProps {
  app: ConnectedApp;
  onRevoke: (clientId: string) => void;
  isRevoking: boolean;
}

const DATE_FORMAT = { year: 'numeric', month: 'short', day: 'numeric' } as const;
const WRITE_SCOPE = 'music24:write';

/** design: ai-assistants a2, a4 (row), a5 (confirm). The row came from connected-apps a1. */
const ConnectedAppRowComponent: React.FC<ConnectedAppRowProps> = ({ app, onRevoke, isRevoking }) => {
  const intl = useIntl();
  const revoke = useCallback(() => onRevoke(app.clientId), [onRevoke, app.clientId]);
  // design: dialogs a5. The dialog stays open with a spinner until the revoke ends.
  const dialog = useConfirmWhileBusy({ isBusy: isRevoking, onConfirm: revoke });

  const revokeLabel = intl.formatMessage(
    { id: 'app.sml.settings.connectedApps.revokeNamed', defaultMessage: 'Revoke {name}' },
    { name: app.clientName }
  );

  return (
    <div className={styles.appRow}>
      <div className={styles.app}>
        <ConnectedAppMark clientId={app.clientId} name={app.clientName} />
        <div className={styles.appText}>
          <b>{app.clientName}</b>
          {app.redirectHost !== null && <small>{app.redirectHost}</small>}
        </div>
      </div>
      <div className={styles.cell}>{intl.formatDate(app.firstApprovedAt, DATE_FORMAT)}</div>
      <div className={app.lastUsedAt === null ? `${styles.cell} ${styles.cellNever}` : styles.cell}>
        {app.lastUsedAt === null
          ? intl.formatMessage({ id: 'app.sml.settings.connectedApps.neverUsed', defaultMessage: 'Never' })
          : intl.formatDate(app.lastUsedAt, DATE_FORMAT)}
      </div>
      <span className={styles.scope}>
        {app.scopes.includes(WRITE_SCOPE)
          ? intl.formatMessage(aiAssistantsMessages.readWrite)
          : intl.formatMessage({ id: 'app.sml.settings.connectedApps.readOnly', defaultMessage: 'Read only' })}
      </span>
      <div className={styles.end}>
        <Button
          color="danger"
          outline
          size="sm"
          onClick={dialog.open}
          disabled={isRevoking}
          title={revokeLabel}
          aria-label={revokeLabel}
        >
          {intl.formatMessage({ id: 'app.sml.settings.connectedApps.revoke', defaultMessage: 'Revoke' })}
        </Button>
        <ConfirmDialog
          isOpen={dialog.isOpen}
          tone="danger"
          mark={
            <ConfirmDialogTileMark withRemoveBadge>
              <ConnectedAppMark clientId={app.clientId} name={app.clientName} />
            </ConfirmDialogTileMark>
          }
          cancelLabel={intl.formatMessage({
            id: 'app.sml.settings.connectedApps.revoke.cancel',
            defaultMessage: 'Cancel',
          })}
          title={intl.formatMessage(
            { id: 'app.sml.settings.connectedApps.revoke.confirmTitle', defaultMessage: 'Revoke {name}?' },
            { name: app.clientName }
          )}
          description={intl.formatMessage(
            {
              id: 'app.sml.settings.connectedApps.revoke.confirmDescription',
              defaultMessage:
                '{name} loses access to this workspace right away. Its tokens stop working. To use it again, connect it again and approve it.',
            },
            { name: app.clientName }
          )}
          confirmLabel={intl.formatMessage({
            id: 'app.sml.settings.connectedApps.revoke.confirmCta',
            defaultMessage: 'Revoke access',
          })}
          isBusy={dialog.isRunning}
          onConfirm={dialog.confirm}
          onCancel={dialog.cancel}
        />
      </div>
    </div>
  );
};

export const ConnectedAppRow = TypedMemo(ConnectedAppRowComponent);
